import {
  LoadableState,
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import FinancingType from '~/constants/FinancingType';
import type { CreditStatus, FinanceInfo, TradeInfo } from '~/contracts';
import { getFinancingType } from './getFinancingType';

type ContextType = {
  financeInfo: FinanceInfo;
  setFinanceInfo: (financeInfo: FinanceInfo | undefined) => void;
  setShouldRefreshSoftPull: React.Dispatch<React.SetStateAction<boolean>>;
  shouldRefreshSoftPull: boolean;
  tradeInfo: LoadableState<TradeInfo>;
  updateSoftPull: (data: CreditStatus) => void;
};

const defaultContext: ContextType = {
  financeInfo: {
    coApplicantToggleOn: undefined,
    coApplicantEligibility: undefined,
    financingType: FinancingType.EstimatedFinancing,
    hasCoApplicant: undefined,
    maxMonthlyPayment: 2000,
    maxTerm: undefined,
    minDownPayment: 0,
    minDownPaymentNoTrade: undefined
  },
  setFinanceInfo() {},
  setShouldRefreshSoftPull() {},
  shouldRefreshSoftPull: false,
  tradeInfo: defaultLoadableState<TradeInfo>({}),
  updateSoftPull() {}
};

const FinanceAndTradeContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  financeInfo?: FinanceInfo;
}>;

export function FinanceAndTradeContextProvider(props: OwnProps) {
  const [financeInfo, setFinanceInfo] = useState(
    props.financeInfo ?? defaultContext.financeInfo
  );
  const loadableTradeInfo = useLoadableState<TradeInfo>({});
  const [shouldRefreshSoftPull, setShouldRefreshSoftPull] = useState(false);

  const setFinanceInfoWithDefault = useCallback(
    (nextFinanceInfo: FinanceInfo | undefined) => {
      setFinanceInfo(nextFinanceInfo ?? defaultContext.financeInfo);
    },
    []
  );

  const updateSoftPull = useCallback((data: CreditStatus) => {
    setFinanceInfo(currentFinanceInfo => ({
      ...currentFinanceInfo,
      financingType: getFinancingType(data)
    }));
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      financeInfo,
      setFinanceInfo: setFinanceInfoWithDefault,
      setShouldRefreshSoftPull,
      shouldRefreshSoftPull,
      tradeInfo: loadableTradeInfo,
      updateSoftPull
    }),
    [
      financeInfo,
      setFinanceInfoWithDefault,
      setShouldRefreshSoftPull,
      shouldRefreshSoftPull,
      loadableTradeInfo,
      updateSoftPull
    ]
  );

  return (
    <FinanceAndTradeContext.Provider value={value}>
      {props.children}
    </FinanceAndTradeContext.Provider>
  );
}

FinanceAndTradeContextProvider.displayName = 'FinanceAndTradeContextProvider';

export function useFinanceInfo(): FinanceInfo {
  return (
    useContext(FinanceAndTradeContext).financeInfo ?? defaultContext.financeInfo
  );
}

export function useFinanceType(): FinancingType | undefined {
  return useFinanceInfo().financingType;
}

export function useHasSoftPull(): boolean {
  return useFinanceType() === FinancingType.UserFinancing;
}

export function useHasExpiredSoftPull(): boolean {
  return useFinanceType() === FinancingType.Expired;
}

export function useTradeInfo(): TradeInfo {
  return useContext(FinanceAndTradeContext).tradeInfo.data;
}

export function useIsTradeInfoLoaded(): boolean {
  return useContext(FinanceAndTradeContext).tradeInfo.isLoaded;
}

export function useShouldRefreshSoftPull(): boolean {
  return useContext(FinanceAndTradeContext).shouldRefreshSoftPull;
}

export default FinanceAndTradeContext;
